import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api, type PolicyPack } from '../../api/client';

const CAPABILITIES = ['read', 'write', 'delete', 'admin'] as const;
const METHODS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'] as const;
const ACTOR_TYPES = ['agent', 'user', 'system'] as const;

const decisionColors: Record<string, string> = {
  allow: 'bg-[rgba(52,211,153,0.12)] text-[var(--color-success)]',
  deny: 'bg-[rgba(248,113,113,0.12)] text-[var(--color-error)]',
  transform: 'bg-[rgba(251,191,36,0.12)] text-[var(--color-warning)]',
  require_approval: 'bg-[rgba(139,92,246,0.12)] text-accent-violet',
};

interface TestResult {
  decision: string;
  rule_name?: string;
  reasons?: string[];
  transformations?: { type: string; target: string; value?: string }[];
  approval?: { scope: string; ttl_seconds: number; reason?: string };
}

export interface PolicyTestPanelProps {
  policy: PolicyPack;
}

export function PolicyTestPanel({ policy }: PolicyTestPanelProps) {
  const { currentWorkspaceId } = useAuth();
  const [toolName, setToolName] = useState('http.request');
  const [capability, setCapability] = useState('read');
  const [method, setMethod] = useState('GET');
  const [url, setUrl] = useState('https://api.example.com/data');
  const [actorId, setActorId] = useState('agent-1');
  const [actorType, setActorType] = useState('agent');
  const [platform, setPlatform] = useState('');
  const [labels, setLabels] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const prevPolicyRef = useRef<PolicyPack | null>(null);

  // Auto-populate test fields from the first rule's conditions when policy changes
  useEffect(() => {
    if (!policy || policy === prevPolicyRef.current) return;
    prevPolicyRef.current = policy;

    const firstRule = policy.rules?.[0];
    if (!firstRule?.conditions) return;

    const c = firstRule.conditions as Record<string, string[] | undefined>;
    if (c.methods?.length) setMethod(c.methods[0]);
    if (c.capabilities?.length) setCapability(c.capabilities[0]);
    if (c.labels?.length) setLabels(c.labels.join(', '));
    if (c.tools?.length) setToolName(c.tools[0]);
    if (c.actor_types?.length) setActorType(c.actor_types[0]);
    if (c.domains?.length) {
      const domain = c.domains[0].replace(/^\*\./, '');
      setUrl(`https://${domain}/resource`);
    }
    if (c.platforms?.length) setPlatform(c.platforms[0]);

    // Clear previous result when policy changes
    setResult(null);
  }, [policy]);

  async function handleTest() {
    if (!currentWorkspaceId) return;
    setRunning(true);
    setError(null);
    setResult(null);

    try {
      const toolCall: Record<string, unknown> = {
        tool_name: toolName,
        capability,
        method,
        url,
        actor_id: actorId,
        actor_type: actorType,
      };
      if (platform.trim()) toolCall.platform = platform.trim();
      if (labels.trim()) toolCall.labels = labels.split(',').map((l) => l.trim()).filter(Boolean);

      const res = await api.evaluateTestPolicy(currentWorkspaceId, policy, toolCall);
      setResult(res.result as unknown as TestResult);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRunning(false);
    }
  }

  const inputClass =
    'w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-violet focus:outline-none focus:ring-1 focus:ring-accent-violet';

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1">Tool Name</label>
          <input
            type="text"
            value={toolName}
            onChange={(e) => setToolName(e.target.value)}
            placeholder="http.request"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1">Capability</label>
          <select value={capability} onChange={(e) => setCapability(e.target.value)} className={inputClass}>
            {CAPABILITIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1">Method</label>
          <select value={method} onChange={(e) => setMethod(e.target.value)} className={inputClass}>
            {METHODS.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1">Actor Type</label>
          <select value={actorType} onChange={(e) => setActorType(e.target.value)} className={inputClass}>
            {ACTOR_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-text-secondary mb-1">URL</label>
        <input
          type="text"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://api.example.com/data"
          className={inputClass}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1">Actor ID</label>
          <input
            type="text"
            value={actorId}
            onChange={(e) => setActorId(e.target.value)}
            placeholder="agent-1"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1">Platform</label>
          <input
            type="text"
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            placeholder="e.g. claude_code"
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-text-secondary mb-1">Labels (comma-separated)</label>
        <input
          type="text"
          value={labels}
          onChange={(e) => setLabels(e.target.value)}
          placeholder="production, critical"
          className={inputClass}
        />
      </div>

      <button
        onClick={handleTest}
        disabled={running || !toolName.trim()}
        className="w-full rounded-lg bg-accent-teal px-4 py-2.5 text-sm font-medium text-text-primary transition-colors hover:opacity-90 disabled:opacity-50"
      >
        {running ? 'Evaluating...' : 'Run Test'}
      </button>

      {error && (
        <div className="rounded-lg bg-[rgba(248,113,113,0.12)] p-3 text-sm text-[var(--color-error)]">
          {error}
        </div>
      )}

      {result && (
        <div className="rounded-lg border border-border-dark bg-surface-1 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-text-secondary">Decision:</span>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${decisionColors[result.decision] || 'bg-surface-2 text-text-primary'}`}>
              {result.decision.toUpperCase()}
            </span>
          </div>

          {result.rule_name && (
            <div>
              <span className="text-xs font-medium text-text-secondary">Matched Rule: </span>
              <span className="text-sm text-text-primary">{result.rule_name}</span>
            </div>
          )}

          {result.reasons && result.reasons.length > 0 && (
            <div>
              <span className="text-xs font-medium text-text-secondary">Reasons:</span>
              <ul className="mt-1 space-y-0.5">
                {result.reasons.map((r, i) => (
                  <li key={i} className="ml-4 list-disc text-xs text-text-secondary">{r}</li>
                ))}
              </ul>
            </div>
          )}

          {result.transformations && result.transformations.length > 0 && (
            <div>
              <span className="text-xs font-medium text-text-secondary">Transformations:</span>
              <ul className="mt-1 space-y-0.5">
                {result.transformations.map((t, i) => (
                  <li key={i} className="ml-4 list-disc text-xs text-text-secondary">
                    {t.type}({t.target}){t.value ? ` = ${t.value}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.approval && (
            <div className="rounded-lg bg-[rgba(139,92,246,0.08)] p-3">
              <span className="text-xs font-medium text-accent-violet">Approval Required</span>
              <div className="mt-1 text-xs text-text-secondary space-y-0.5">
                <p>Scope: {result.approval.scope}</p>
                <p>TTL: {result.approval.ttl_seconds}s</p>
                {result.approval.reason && <p>Reason: {result.approval.reason}</p>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
